/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {PullRequestReviewDecision, PullRequestState} from './generated/graphql';
import {hasLandedMarker} from './landedMarker';
import {Box, Text} from '@primer/react';

interface StatusStyle {
  label: string;
  fg: string;
  bg: string;
  border: string;
}

export default function PullRequestStateLabel({
  reviewDecision,
  state,
  isDraft,
  lastCommentBody,
}: {
  reviewDecision: PullRequestReviewDecision | null;
  state: PullRequestState;
  isDraft?: boolean;
  lastCommentBody?: string | null;
  variant?: 'small' | 'normal';
}) {
  const closedAsLanded = state === PullRequestState.Closed && hasLandedMarker(lastCommentBody);
  const style = closedAsLanded
    ? {label: 'Landed', fg: '#3d8b37', bg: '#3d8b3718', border: '#3d8b3740'}
    : isDraft && state === PullRequestState.Open
      ? {label: 'Draft', fg: '#6e7781', bg: '#6e778114', border: '#6e778140'}
      : statusStyle(state, reviewDecision);
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: '7px',
        py: '2px',
        borderRadius: '4px',
        bg: style.bg,
        border: '1px solid',
        borderColor: style.border,
        fontSize: '11px',
        fontWeight: 600,
        lineHeight: '16px',
        whiteSpace: 'nowrap',
      }}>
      <Text sx={{color: style.fg}}>{style.label}</Text>
    </Box>
  );
}

function statusStyle(
  state: PullRequestState,
  reviewDecision: PullRequestReviewDecision | null,
): StatusStyle {
  switch (state) {
    case PullRequestState.Closed:
      return {label: 'Abandoned', fg: '#8b949e', bg: '#8b949e14', border: '#8b949e33'};
    case PullRequestState.Merged:
      return {label: 'Landed', fg: '#3d8b37', bg: '#3d8b3718', border: '#3d8b3740'};
    case PullRequestState.Open: {
      if (reviewDecision === null) {
        return {label: 'Needs Review', fg: '#9a6700', bg: '#9a670014', border: '#9a670040'};
      }
      return reviewDecisionStyle(reviewDecision);
    }
  }
}

function reviewDecisionStyle(reviewDecision: PullRequestReviewDecision): StatusStyle {
  switch (reviewDecision) {
    case PullRequestReviewDecision.Approved:
      return {label: 'Accepted', fg: '#1a7f37', bg: '#1a7f3718', border: '#1a7f3740'};
    case PullRequestReviewDecision.ChangesRequested:
      return {label: 'Needs Revision', fg: '#cf222e', bg: '#cf222e14', border: '#cf222e40'};
    case PullRequestReviewDecision.ReviewRequired:
      return {label: 'Needs Review', fg: '#9a6700', bg: '#9a670014', border: '#9a670040'};
  }
}
